import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_close_default } from '@repo/icons/index'
import { useEffect, useState } from 'react'
import Speech from 'speak-tts'
import { IExpression } from '../../../model/lesson.interface'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { n_common, n_material, s_lesson } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { ICON_PLAY_BUTTON_SMALL_PRIMARY } from '../../../modules/svg/svg'

interface ExpressionBodyProps {
  expressionData: IExpression[]
  currentHash: any
  onClose?: () => void
}

const ExpressionBody = ({ expressionData, currentHash, onClose }: ExpressionBodyProps) => {
  const [activeIndex, setActiveIndex] = useState(currentHash - 1)
  const [speech, setSpeech] = useState(null)
  const [voice, setVoice] = useState(null)
  const [initializing, setInitializing] = useState(true)

  const { t, isLocaleKO } = useTranslation()
  const router = useAppRouter()
  const isWebview = useIsWebview()

  const handleSetVoice = (voices) => {
    const GOOGLE_US_EN_VOICE = 'Google US English'
    const SAMANTHA_VOICE = 'Samantha'

    const googleVoice = voices.find((voice) => voice.name === GOOGLE_US_EN_VOICE)
    const samanthaVoice = voices.find((voice) => voice.name === SAMANTHA_VOICE)
    const enUsVoice = voices.find((voice) => voice.lang === 'en-US')

    const selectedVoice = googleVoice || samanthaVoice || enUsVoice || voices[0]
    console.log('Voice selected:', selectedVoice)
    setVoice(selectedVoice)
  }

  useEffect(() => {
    const initializeSpeech = async () => {
      const speechInstance = new Speech()
      try {
        await speechInstance.init({
          volume: 1,
          lang: 'en-US',
          rate: 1,
          pitch: 1,
          splitSentences: true,
        })
        console.log('Speech initialized successfully')
        setSpeech(speechInstance)

        if (typeof window !== 'undefined' && window.speechSynthesis) {
          const loadVoices = () => {
            const voices = window.speechSynthesis.getVoices()
            if (voices.length > 0) {
              handleSetVoice(voices)
              setInitializing(false)
            } else {
              console.log('No voices available yet, waiting...')
            }
          }

          if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = loadVoices
          } else {
            loadVoices()
          }
        } else {
          console.warn('Speech synthesis API is not available in this environment.')
          setInitializing(false)
        }
      } catch (error) {
        console.error('Failed to initialize speech:', error)
        setInitializing(false)
      }
    }

    initializeSpeech()
  }, [])

  const readText = (text) => {
    if (speech && voice) {
      console.log('voice:', voice.name)
      speech.speak({
        text,
        queue: false,
        voice,
        listeners: {
          onstart: () => console.log('Text reading started'),
          onend: () => console.log('Text reading finished'),
          onerror: (e) => console.error('Error while reading text:', e),
        },
      })
    } else {
      console.log('Speech not initialized or voice not set.')
    }
  }

  const handleListen = (text) => {
    if (isWebview) {
      router.tts(text)
    } else {
      readText(text)
    }
  }

  const handlePrevButton = () => {
    setActiveIndex((prev) => prev - 1)
  }

  const handleNextButton = () => {
    setActiveIndex((prev) => prev + 1)
  }

  return (
    <div className="flex h-full flex-col justify-between">
      <div className="">
        <div className="flex flex-row justify-between p-[20px]">
          <div className="flex flex-row">
            <div className="Title1-20Bd">{t(n_material.expressions_title)}</div>
            <div className="Title1-20Md ml-[8px] flex flex-row text-gray-500">
              <div className="">{activeIndex + 1}/</div>
              <div className="">{expressionData?.length}</div>
            </div>
          </div>
          {onClose && <QIcon_close_default className="cursor-pointer" onClick={() => onClose()} />}
        </div>
        <div className="Headline2-24Bd py-[16px] px-[20px] text-purple-400">
          <div className="">{expressionData[activeIndex]?.expression}</div>
          {isLocaleKO ? (
            <>
              <div className="Body1-16Md my-[4px] text-black">{expressionData[activeIndex]?.meaning?.ko}</div>
              <div className="Body1-16Md text-black">{expressionData[activeIndex]?.meaning?.en}</div>
            </>
          ) : (
            <>
              <div className="Body1-16Md text-black">{expressionData[activeIndex]?.meaning?.en}</div>
            </>
          )}
        </div>
        <div className="">
          {expressionData[activeIndex]?.examples?.map((example, j) => (
            <div key={`example-${j}`} className="py-[16px] px-[20px]">
              <div className="flex items-center font-medium">
                <div className="Body1-16Bd mr-[4px]">{t(s_lesson.example)}</div>
                <div className="mr-[4px]">{j + 1}</div>
                <button onClick={() => handleListen(example.text)}>
                  <img src={ICON_PLAY_BUTTON_SMALL_PRIMARY} alt="play" className="h-[24px] w-[24px]" />
                </button>
              </div>
              <div key={j} className="Body1-16Md">
                <p className="my-[4px]">{example.text}</p>
                {isLocaleKO && <p className="mb-0">{example.meaning}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-row gap-[8px] px-[20px] pb-[20px]">
        <DSButton
          type={BUTTON_TYPE.SOLID}
          size={BUTTON_SIZE.MD}
          onClick={handlePrevButton}
          disabled={activeIndex === 0}
        >
          <DSButton.Label>{t(n_common.btn_back)}</DSButton.Label>
        </DSButton>
        <DSButton
          type={BUTTON_TYPE.OUTLINE}
          size={BUTTON_SIZE.MD}
          onClick={handleNextButton}
          disabled={activeIndex === expressionData?.length - 1}
        >
          <DSButton.Label>{t(n_common.btn_next)}</DSButton.Label>
        </DSButton>
      </div>
    </div>
  )
}

export default ExpressionBody
